import React, { useState, useRef, useEffect, useCallback } from 'react';
import { callsAPI } from '../services/api';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, PhoneOff, Mic, MicOff, Video, VideoOff, 
  Volume2, ShieldAlert, Loader2, Sparkles,
  MonitorUp, Minimize2, Maximize2, RefreshCw, HelpCircle
} from 'lucide-react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useCall } from '../contexts/CallContext';

const VideoCall = ({ call, currentUserId, partner, onEnd, ws }) => {
  const { subscribeChat } = useWebSocket();
  const { registerSignalHandler, unregisterSignalHandler } = useCall();
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const isShowRemoteVideo = call.type === 'video' && remoteStream;
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(call.type === 'voice');
  const [status, setStatus] = useState(call.status || 'connecting');
  const [duration, setDuration] = useState(0);
  const [warningMsg, setWarningMsg] = useState('');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [e2eEmojis, setE2eEmojis] = useState([]);
  
  const [viewState, setViewState] = useState('window'); // 'bubble' | 'window' | 'fullscreen'
  const [videoFit, setVideoFit] = useState('cover'); // 'cover' | 'contain'
  const [connectionStep, setConnectionStep] = useState('initializing');
  const [dragLimits, setDragLimits] = useState({ left: -600, right: 50, top: -600, bottom: 50 });
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimeoutRef = useRef(null);

  const [audioVolume, setAudioVolume] = useState(0);
  const audioVolumeRef = useRef(0);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteVideoBgRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerConnection = useRef(null);
  const timerRef = useRef(null);
  const ringingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioSourceRef = useRef(null);
  const pendingSignalsRef = useRef([]);
  const pendingCandidatesRef = useRef([]);
  const isAcceptedRef = useRef(call.status === 'connected');
  const isCaller = call.callerId === currentUserId;

  useEffect(() => {
    if (call.status === 'connected') {
      isAcceptedRef.current = true;
    }
  }, [call.status]);

  useEffect(() => {
    audioVolumeRef.current = audioVolume;
  }, [audioVolume]);

  // Window resize handler to switch mobile layouts
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide controls in fullscreen / mobile layout
  useEffect(() => {
    if (viewState !== 'fullscreen' && !isMobile) {
      setControlsVisible(true);
      return;
    }

    const showControls = () => {
      setControlsVisible(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    };

    // Show controls initially
    showControls();

    const handleMouseMove = () => {
      showControls();
    };

    const handleTouchStart = () => {
      showControls();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [viewState, isMobile]);

  const stepLabels = {
    initializing: 'Инициализация подключения...',
    fetching_ice: 'Запрос сетевых конфигураций...',
    acquiring_media: 'Запрос доступа к медиа-устройствам...',
    creating_peer: 'Создание канала связи P2P...',
    signaling: 'Обмен сигнальными ключами...',
    connecting: 'Установление прямого соединения...'
  };

  // Drag constraints limit calculation
  useEffect(() => {
    const updateLimits = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (viewState === 'window') {
        setDragLimits({
          left: -w + 350 + 48,
          right: 20,
          top: -h + 490 + 48,
          bottom: 20
        });
      } else if (viewState === 'bubble') {
        setDragLimits({
          left: -w + 64 + 48,
          right: 20,
          top: -h + 64 + 48,
          bottom: 20
        });
      }
    };
    updateLimits();
    window.addEventListener('resize', updateLimits);
    return () => window.removeEventListener('resize', updateLimits);
  }, [viewState]);

  // ─── 1. WebRTC & Duration Loops ───
  useEffect(() => {
    initCall();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.startsWith('connected')) {
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [status]);

  // Ringing timeout (20 seconds if offline, 30 if online)
  useEffect(() => {
    if (status === 'connecting' && isCaller) {
      const isTargetOnline = call.targetOnline;
      const timeoutMs = isTargetOnline ? 30000 : 20000;
      
      ringingTimeoutRef.current = setTimeout(() => {
        if (status === 'connecting') {
          setWarningMsg(isTargetOnline ? "Пользователь не отвечает." : "Пользователь не в сети.");
          setTimeout(() => {
            handleEnd();
          }, 2000);
        }
      }, timeoutMs);
    } else {
      if (ringingTimeoutRef.current) {
        clearTimeout(ringingTimeoutRef.current);
      }
    }
    return () => {
      if (ringingTimeoutRef.current) {
        clearTimeout(ringingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // WebAudio API Voice Visualizer
  useEffect(() => {
    const streamToAnalyze = localStream;
    if (!streamToAnalyze) return;

    const audioTracks = streamToAnalyze.getAudioTracks();
    if (audioTracks.length === 0) return;

    let audioContext;
    let analyser;
    let source;
    let animationId;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      audioContext = new AudioContextClass();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      source = audioContext.createMediaStreamSource(streamToAnalyze);
      source.connect(analyser);

      // Workaround for Chrome/Chromium silencing HTML5 audio element
      try {
        const dummyGain = audioContext.createGain();
        dummyGain.gain.value = 0;
        analyser.connect(dummyGain);
        dummyGain.connect(audioContext.destination);
      } catch (err) {
        console.warn("Failed to apply Chrome Web Audio silencing workaround:", err);
      }

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      audioSourceRef.current = source;

      if (audioContext.state === 'suspended') {
        const resume = () => {
          if (audioContext.state === 'suspended') {
            audioContext.resume().catch(() => {});
          }
          window.removeEventListener('click', resume);
        };
        window.addEventListener('click', resume);
      }

      let lastVolume = 0;
      const updateVolume = () => {
        if (!analyser) return;
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        const targetVolume = Math.min(100, Math.round((average / 120) * 100));
        lastVolume = lastVolume * 0.7 + targetVolume * 0.3;
        setAudioVolume(lastVolume);
        
        animationId = requestAnimationFrame(updateVolume);
      };
      
      updateVolume();
    } catch (e) {
      console.warn("WebAudio API visualizer failed to initialize:", e);
    }

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (source) source.disconnect();
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
      setAudioVolume(0);
    };
  }, [localStream]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.play().catch(() => {});
    }
  }, [localStream, viewState, videoOff]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(() => {});
    }
  }, [remoteStream, viewState, isShowRemoteVideo]);

  useEffect(() => {
    if (remoteVideoBgRef.current && remoteStream) {
      remoteVideoBgRef.current.srcObject = remoteStream;
      remoteVideoBgRef.current.play().catch(() => {});
    }
  }, [remoteStream, viewState, isShowRemoteVideo, videoFit]);

  useEffect(() => {
    if (remoteAudioRef.current) {
      const playAudio = !!remoteStream;
      remoteAudioRef.current.srcObject = playAudio ? remoteStream : null;
      if (playAudio) {
        remoteAudioRef.current.play().catch(err => {
          console.warn("Failed to play remote audio stream automatically:", err);
        });
      }
    }
  }, [remoteStream]);

  const sendOffer = useCallback(async () => {
    const pc = peerConnection.current;
    if (!pc) return;
    try {
      setConnectionStep('signaling');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await callsAPI.signal(call.id, { type: 'offer', sdp: offer }).catch(() => {});
      setConnectionStep('connecting');
    } catch (err) {
      console.error("Failed to send offer:", err);
    }
  }, [call.id]);

  // WebRTC ICE connection restart mechanism
  const triggerIceRestart = async () => {
    const pc = peerConnection.current;
    if (!pc) return;
    try {
      console.log("[WebRTC] Initiating ICE connection restart...");
      setWarningMsg("Сеть нестабильна. Восстановление...");
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      await callsAPI.signal(call.id, { type: 'offer', sdp: offer }).catch(() => {});
    } catch (err) {
      console.warn("ICE Restart offer creation failed:", err);
    }
  };

  const handleSignal = useCallback(async (signal) => {
    const pc = peerConnection.current;
    if (!pc) {
      console.log("Queueing signaling message (PeerConnection not ready):", signal.type);
      pendingSignalsRef.current.push(signal);
      return;
    }
    try {
      if (signal.type === 'offer') {
        console.log("Processing WebRTC offer");
        await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
        console.log("Setting remote description (offer). Processing queued candidates:", pendingCandidatesRef.current.length);
        while (pendingCandidatesRef.current.length > 0) {
          const candidate = pendingCandidatesRef.current.shift();
          await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(err => {
            console.warn("Failed to add queued candidate:", err);
          });
        }
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await callsAPI.signal(call.id, { type: 'answer', sdp: answer }).catch(() => {});
      } else if (signal.type === 'answer') {
        console.log("Processing WebRTC answer");
        await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
        console.log("Setting remote description (answer). Processing queued candidates:", pendingCandidatesRef.current.length);
        while (pendingCandidatesRef.current.length > 0) {
          const candidate = pendingCandidatesRef.current.shift();
          await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(err => {
            console.warn("Failed to add queued candidate:", err);
          });
        }
      } else if (signal.type === 'candidate') {
        if (!pc.remoteDescription) {
          console.log("Queueing candidate because remoteDescription is not set yet");
          pendingCandidatesRef.current.push(signal.candidate);
        } else {
          await pc.addIceCandidate(new RTCIceCandidate(signal.candidate)).catch(err => {
            console.warn("Failed to add candidate:", err);
          });
        }
      }
    } catch (err) {
      console.error('Signal handling error:', err);
    }
  }, [call.id]);

  useEffect(() => {
    const unsub = subscribeChat((wsMsg) => {
      if (wsMsg.type === 'call_accepted' && wsMsg.callId === call.id) {
        setStatus('connected');
        isAcceptedRef.current = true;
        sendOffer();
      }
      if ((wsMsg.type === 'call_ended' || wsMsg.type === 'call_rejected') && wsMsg.callId === call.id) {
        cleanup();
        onEnd();
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeChat, call.id, sendOffer]);

  useEffect(() => {
    registerSignalHandler(call.id, (signal) => {
      handleSignal(signal);
    });
    return () => {
      unregisterSignalHandler();
    };
  }, [call.id, handleSignal, registerSignalHandler, unregisterSignalHandler]);

  // ─── 3. WebRTC Functions ───
  const initCall = async () => {
    try {
      setPermissionBlocked(false);
      setConnectionStep('fetching_ice');
      let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
      try {
        const turnRes = await api.get('/turn/credentials');
        if (turnRes.data?.iceServers) iceServers = turnRes.data.iceServers;
      } catch {}

      setConnectionStep('acquiring_media');
      const isVideo = call.type === 'video';
      let stream = null;
      let fallbackMode = false;
      let noDevicesMode = false;

      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: true, 
          video: isVideo ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false 
        });
      } catch (err) {
        console.warn("Camera/microphone failure, trying gracefully to fallback...", err);
        // Check if permissions were denied
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionBlocked(true);
          return;
        }

        if (isVideo) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            fallbackMode = true;
            setWarningMsg("Камера недоступна. Звонок переведен в аудио-режим.");
            setVideoOff(true);
          } catch (audioErr) {
            console.error("Audio fallback failed too:", audioErr);
            noDevicesMode = true;
          }
        } else {
          noDevicesMode = true;
        }
      }

      if (noDevicesMode) {
        setWarningMsg("Устройства ввода не найдены. Подключение в режиме прослушивания.");
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const dst = ctx.createMediaStreamDestination();
          osc.connect(dst);
          stream = dst.stream;
        } catch (e) {
          console.error("Failed to generate fallback silent stream:", e);
        }
      }

      setConnectionStep('creating_peer');
      setLocalStream(stream);

      const pc = new RTCPeerConnection({ iceServers });
      peerConnection.current = pc;

      pc.ontrack = (event) => {
        console.log("[WebRTC] Received remote track:", event.track.kind);
        let stream = event.streams[0];
        if (!stream) {
          stream = new MediaStream([event.track]);
        }
        
        setRemoteStream(prev => {
          if (prev) {
            // Check if track is already in the stream to prevent duplicates
            if (!prev.getTracks().find(t => t.id === event.track.id)) {
              prev.addTrack(event.track);
            }
            return new MediaStream(prev.getTracks());
          }
          return new MediaStream(stream.getTracks());
        });

        setStatus('connected');
        setConnectionStep('connected');
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          callsAPI.signal(call.id, { type: 'candidate', candidate: event.candidate }).catch(() => {});
        }
      };

      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        console.log("ICE Connection State:", state);
        if (state === 'disconnected') {
          setWarningMsg("Сеть нестабильна. Переподключение...");
          if (isCaller) {
            triggerIceRestart();
          }
        } else if (state === 'failed') {
          setWarningMsg("Соединение разорвано. Восстановление...");
          if (isCaller) {
            triggerIceRestart();
          } else {
            // Set 12 second reconnection window for receiver
            reconnectTimeoutRef.current = setTimeout(() => {
              setWarningMsg("Ошибка подключения. Завершение вызова...");
              setTimeout(handleEnd, 2000);
            }, 12000);
          }
        } else if (state === 'connected' || state === 'completed') {
          setWarningMsg("");
          setStatus('connected');
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        }
      };

      if (stream) {
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      }

      // Process any pending signaling messages queued while initializing
      console.log("Peer connection initialized, processing queued signals:", pendingSignalsRef.current.length);
      const pendingSignals = [...pendingSignalsRef.current];
      pendingSignalsRef.current = [];
      for (const sig of pendingSignals) {
        await handleSignal(sig);
      }

      const isCallAccepted = isAcceptedRef.current || call.status === 'connected';
      if (isCaller && isCallAccepted) {
        setConnectionStep('signaling');
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await callsAPI.signal(call.id, { type: 'offer', sdp: offer }).catch(() => {});
        setConnectionStep('connecting');
      } else {
        setConnectionStep('connecting');
      }

      if (noDevicesMode) {
        setStatus('connected-listen');
      } else if (fallbackMode) {
        setStatus('connected-voice');
      } else {
        setStatus(call.status || 'connecting');
      }

    } catch (err) {
      console.error('Call initialization crashed:', err);
      setStatus('failed');
    }
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(t => { t.enabled = muted; });
      setMuted(!muted);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const isCurrentlyOff = videoOff;
      localStream.getVideoTracks().forEach(t => { t.enabled = isCurrentlyOff; });
      setVideoOff(!isCurrentlyOff);
    }
  };

  const toggleScreenShare = async () => {
    if (call.type !== 'video') return;
    try {
      if (isScreenSharing) {
        const newStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const newVideoTrack = newStream.getVideoTracks()[0];
        
        const currentVideoTrack = localStream.getVideoTracks()[0];
        if (currentVideoTrack) currentVideoTrack.stop();
        
        localStream.removeTrack(currentVideoTrack);
        localStream.addTrack(newVideoTrack);
        
        const sender = peerConnection.current.getSenders().find(s => s.track.kind === 'video');
        if (sender) sender.replaceTrack(newVideoTrack);
        
        setIsScreenSharing(false);
      } else {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = displayStream.getVideoTracks()[0];
        
        screenTrack.onended = () => {
          if (peerConnection.current) toggleScreenShare();
        };
        
        const currentVideoTrack = localStream.getVideoTracks()[0];
        if (currentVideoTrack) currentVideoTrack.stop();
        
        localStream.removeTrack(currentVideoTrack);
        localStream.addTrack(screenTrack);
        
        const sender = peerConnection.current.getSenders().find(s => s.track.kind === 'video');
        if (sender) sender.replaceTrack(screenTrack);
        
        setIsScreenSharing(true);
      }
    } catch (err) {
      console.error("Screen sharing error:", err);
      setWarningMsg("Ошибка при доступе к экрану");
      setTimeout(() => setWarningMsg(""), 3000);
    }
  };

  useEffect(() => {
    if (call.id) {
      const emojis = ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐧", "🐦", "🐤", "🦆", "🦅", "🦉", "🦇", "🐺", "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌", "🐞", "🐜", "🦟", "🐢", "🐍", "🦎", "🦖", "🦕", "🐙", "🦑", "🦐", "🦞", "🦀", "🐡", "🐠", "🐟", "🐬", "🐳", "🐋", "🦈", "🐊", "🐅", "🐆", "🦓", "🦍", "🦧", "🐘", "🦛", "🦏", "🐪", "🐫", "🦒", "🦘", "🐃", "🐂", "🐄", "🐎", "🐖", "🐏", "🐑", "🦙", "🐐", "🦌", "🐕", "🐩", "🦮", "🐕‍🦺", "🐈", "🐈‍⬛", "🐓", "🦃", "🦚", "🦜", "🦢", "🦩", "🕊️", "🐇", "🦝", "🦨", "🦡", "🦦", "🦥", "🐁", "🐀", "🐿️", "🦔"];
      let hash = 0;
      for (let i = 0; i < call.id.length; i++) {
        hash = ((hash << 5) - hash) + call.id.charCodeAt(i);
        hash |= 0;
      }
      const randomSeed = Math.abs(hash);
      setE2eEmojis([
        emojis[randomSeed % emojis.length],
        emojis[(randomSeed * 2) % emojis.length],
        emojis[(randomSeed * 3) % emojis.length],
        emojis[(randomSeed * 4) % emojis.length]
      ]);
    }
  }, [call.id]);

  const handleEnd = async () => {
    try { await callsAPI.endCall(call.id); } catch {}
    cleanup();
    onEnd();
  };

  const cleanup = () => {
    clearInterval(timerRef.current);
    if (ringingTimeoutRef.current) clearTimeout(ringingTimeoutRef.current);
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (localStream) localStream.getTracks().forEach(t => t.stop());
    if (peerConnection.current) peerConnection.current.close();
    peerConnection.current = null;
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const getStepProgress = (step) => {
    switch (step) {
      case 'initializing': return 15;
      case 'fetching_ice': return 35;
      case 'acquiring_media': return 55;
      case 'creating_peer': return 75;
      case 'signaling': return 90;
      case 'connecting': return 98;
      default: return 0;
    }
  };

  const displayName = partner?.displayName || 'Пользователь VeonFox';
  const username = partner?.username || 'user';
  const avatarUrl = partner?.avatar || 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>';
  
  const isAiAvatar = partner?.avatar && (partner.avatar.startsWith('ai-avatar:') || partner.avatar.startsWith('linear-gradient'));
  const avatarStyle = isAiAvatar ? { background: partner.avatar.startsWith('ai-avatar:') ? partner.avatar.substring(10) : partner.avatar } : {};

  // Build responsive container classes
  let containerClasses = "fixed z-[100] font-sans overflow-hidden text-white shadow-2xl flex flex-col justify-between ";
  if (viewState === 'bubble') {
    containerClasses += "bottom-6 right-6 w-16 h-16 rounded-full border border-white/20 ring-2 ring-sky-500/40 bg-zinc-950 cursor-pointer hover:scale-105 transition-transform duration-200";
  } else if (isMobile || viewState === 'fullscreen') {
    // If mobile or full screen, force fullscreen bounds
    containerClasses += "inset-0 w-full h-full bg-[#151516]";
  } else {
    // Standard desktop floating window
    containerClasses += `bottom-6 right-6 w-[350px] h-[490px] rounded-2xl border border-white/10 bg-[#1c1c1d] shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-shadow duration-300`;
  }

  const isFluidLayout = isMobile || viewState === 'fullscreen';

  return (
    <motion.div 
      layout
      drag={viewState !== 'fullscreen' && !isMobile}
      dragConstraints={dragLimits}
      dragElastic={0.05}
      dragMomentum={false}
      className={containerClasses}
      data-testid="video-call"
    >
      <audio 
        ref={remoteAudioRef} 
        autoPlay 
        playsInline 
        style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }} 
      />

      {/* Permission blocked overlay */}
      <AnimatePresence>
        {permissionBlocked && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-zinc-950/90 backdrop-blur-xl z-[130] flex flex-col items-center justify-center p-6 text-center select-none"
          >
            <div className="w-14 h-14 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-500 mb-4 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
              <ShieldAlert className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-lg text-white mb-2 font-unbounded">Доступ заблокирован</h3>
            <p className="text-sm text-zinc-400 max-w-xs leading-relaxed mb-6">
              Пожалуйста, разрешите доступ к микрофону и камере в настройках вашего браузера для совершения вызовов.
            </p>
            <div className="flex flex-col gap-2.5 w-full max-w-[200px]">
              <button 
                onClick={initCall}
                className="py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium flex items-center justify-center gap-2 transition-colors cursor-pointer text-sm shadow-md"
              >
                <RefreshCw className="w-4 h-4" /> Повторить
              </button>
              <button 
                onClick={handleEnd}
                className="py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium transition-colors cursor-pointer text-sm"
              >
                Отмена
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {viewState === 'bubble' ? (
        <div 
          className="w-full h-full relative group flex items-center justify-center rounded-full overflow-hidden"
          onClick={() => setViewState('window')}
          title="Развернуть звонок"
        >
          {isShowRemoteVideo ? (
            <video 
              ref={remoteVideoRef} 
              autoPlay 
              playsInline 
              muted={true}
              className="w-full h-full object-cover scale-x-[-1]" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-900">
              {isAiAvatar ? (
                <div style={avatarStyle} className="w-full h-full flex items-center justify-center text-white font-bold text-lg uppercase font-unbounded">
                  {displayName.substring(0, 2)}
                </div>
              ) : (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              )}
            </div>
          )}
          {muted && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-rose-500 rounded-full z-20">
              <MicOff className="w-4 h-4" />
            </div>
          )}
          <div className="absolute inset-0 rounded-full border border-sky-400/40 animate-pulse pointer-events-none" />
        </div>
      ) : (
        <>
          {/* Header Controls */}
          <div className={`w-full flex items-center justify-between px-5 py-4 z-50 absolute top-0 transition-all duration-300 ${
            isFluidLayout ? 'bg-gradient-to-b from-black/45 to-transparent' : 'bg-black/20'
          } ${
            isFluidLayout && !controlsVisible ? 'opacity-0 -translate-y-4 pointer-events-none' : 'opacity-100 translate-y-0'
          }`}>
            {/* Native close action buttons inside floating card */}
            {!isFluidLayout ? (
              <div className="flex items-center justify-between w-full pointer-events-auto">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono font-semibold text-white/50">E2EE</span>
                  {status.startsWith('connected') && (
                    <span className="text-[10px] font-mono font-semibold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 shadow-[0_0_8px_rgba(16,185,129,0.1)]">
                      {formatTime(duration)}
                    </span>
                  )}
                  {isShowRemoteVideo && (
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => { e.stopPropagation(); setVideoFit(f => f === 'cover' ? 'contain' : 'cover'); }}
                      className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors text-[10px] font-sans font-semibold cursor-pointer"
                      title={videoFit === 'cover' ? "Вписать видео" : "Заполнить экран"}
                    >
                      {videoFit === 'cover' ? "Вписать" : "Заполнить"}
                    </motion.button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <motion.button 
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); setViewState('bubble'); }} 
                    className="p-1 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors cursor-pointer"
                    title="Свернуть в кружок"
                  >
                    <Minimize2 className="w-3.5 h-3.5" />
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); setViewState('fullscreen'); }} 
                    className="p-1 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors cursor-pointer"
                    title="Во весь экран"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); handleEnd(); }} 
                    className="p-1 rounded bg-rose-500/10 hover:bg-rose-500 text-rose-400 hover:text-white transition-colors cursor-pointer"
                    title="Завершить звонок"
                  >
                    <PhoneOff className="w-3.5 h-3.5" />
                  </motion.button>
                </div>
              </div>
            ) : (
              /* Premium fullscreen top-header controls */
              <div className="flex items-center justify-between w-full pointer-events-auto">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                  <span className="text-xs font-semibold text-white/70 uppercase tracking-widest font-mono">E2EE Secured</span>
                  {status.startsWith('connected') && (
                    <span className="ml-2 text-xs font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.15)] flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {formatTime(duration)}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  {isShowRemoteVideo && (
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => { e.stopPropagation(); setVideoFit(f => f === 'cover' ? 'contain' : 'cover'); }}
                      className="p-2 py-1.5 rounded-xl bg-black/45 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-all cursor-pointer flex items-center justify-center gap-1.5 text-xs font-semibold font-sans"
                      title={videoFit === 'cover' ? "Вписать видео" : "Заполнить экран"}
                    >
                      {videoFit === 'cover' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                      <span>{videoFit === 'cover' ? "Вписать" : "Заполнить"}</span>
                    </motion.button>
                  )}
                  {e2eEmojis.length === 4 && (
                    <div className="flex items-center gap-1.5 bg-black/45 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/10" title="Шифр безопасности (E2EE)">
                      {e2eEmojis.map((emoji, idx) => <span key={idx} className="text-sm">{emoji}</span>)}
                    </div>
                  )}
                  {!isMobile && (
                    <motion.button 
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.92 }}
                      onClick={() => setViewState('window')}
                      className="p-2.5 rounded-xl bg-black/45 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-all cursor-pointer"
                    >
                      <Minimize2 className="w-4.5 h-4.5" />
                    </motion.button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Warning Message Bar */}
          <AnimatePresence>
            {warningMsg && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }} 
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="absolute top-16 left-1/2 -translate-x-1/2 max-w-[90%] z-50 px-4 py-2 bg-rose-500/20 backdrop-blur-md border border-rose-500/30 rounded-full text-xs text-white flex items-center gap-2 shadow-[0_4px_15px_rgba(239,68,68,0.15)]"
              >
                <span className="flex-1 text-center font-medium font-sans">{warningMsg}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative flex-1 w-full h-full flex flex-col items-center justify-between overflow-hidden">
            
            {/* Ambient Blurred Avatar canvas in the background */}
            {!isShowRemoteVideo && (
              <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none select-none">
                {isAiAvatar ? (
                  <div style={avatarStyle} className="absolute inset-0 filter blur-[80px] opacity-[0.18] scale-125" />
                ) : (
                  <img src={avatarUrl} alt="" className="absolute inset-0 w-full h-full object-cover filter blur-[80px] opacity-[0.18] scale-125" />
                )}
              </div>
            )}

            {/* Remote Video Stream */}
            {isShowRemoteVideo ? (
              <div className="absolute inset-0 w-full h-full bg-black flex items-center justify-center overflow-hidden z-0">
                {/* Background blurred ambient video for letterboxes */}
                {videoFit === 'contain' && (
                  <video 
                    ref={remoteVideoBgRef}
                    autoPlay 
                    playsInline 
                    muted={true}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 filter blur-xl scale-110 pointer-events-none" 
                  />
                )}
                <video 
                  ref={remoteVideoRef} 
                  autoPlay 
                  playsInline 
                  muted={true}
                  className={`w-full h-full z-10 ${
                    videoFit === 'contain' ? 'object-contain' : 'object-cover'
                  }`} 
                />
              </div>
            ) : (
              /* Voice Call / Ringing screen with HSL Pulsing Aura circles */
              <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-b from-[#1c1c1d]/50 to-[#121213]">
                
                {/* Pulsing visualizer halos */}
                <div className={`relative flex items-center justify-center transition-all ${
                  isFluidLayout ? 'w-36 h-36 mb-6' : 'w-24 h-24 mb-4'
                }`}>
                  {/* Layered glowing voice indicators */}
                  <div 
                    className="absolute inset-0 rounded-full bg-sky-500/10 transition-all duration-75 ease-out" 
                    style={{ transform: `scale(${1 + (audioVolume / 100) * 0.5})`, opacity: 0.15 + (audioVolume / 100) * 0.5 }} 
                  />
                  <div 
                    className="absolute inset-2 rounded-full bg-cyan-500/15 transition-all duration-75 ease-out" 
                    style={{ transform: `scale(${1 + (audioVolume / 100) * 0.35})`, opacity: 0.25 + (audioVolume / 100) * 0.4 }} 
                  />
                  <div 
                    className="absolute inset-4 rounded-full bg-indigo-500/20 transition-all duration-75 ease-out" 
                    style={{ transform: `scale(${1 + (audioVolume / 100) * 0.2})`, opacity: 0.35 + (audioVolume / 100) * 0.3 }} 
                  />

                  {/* Core Photo */}
                  <div className="w-full h-full rounded-full overflow-hidden relative z-10 bg-zinc-800 flex items-center justify-center shadow-[0_10px_35px_rgba(0,0,0,0.4)] border border-white/10">
                    {isAiAvatar ? (
                      <div style={avatarStyle} className="w-full h-full flex items-center justify-center text-white font-bold text-4xl uppercase font-unbounded">
                        {displayName.substring(0, 2)}
                      </div>
                    ) : (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    )}
                  </div>
                </div>
                
                {/* Partner Metadata */}
                <div className="text-center z-10 px-4">
                  <h3 className={`font-semibold tracking-tight text-white font-unbounded ${
                    isFluidLayout ? 'text-2xl' : 'text-lg'
                  }`}>
                    {displayName}
                  </h3>
                  <div className="mt-2 flex items-center justify-center text-zinc-400 font-medium text-xs tracking-wide w-full">
                    {status === 'connecting' ? (
                      <div className="flex flex-col items-center gap-3 mt-2 w-full max-w-[240px]">
                        <span className="text-zinc-400 text-xs font-semibold text-center animate-pulse min-h-[1.5rem] tracking-wide font-sans flex items-center justify-center gap-1.5">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-400" />
                          {stepLabels[connectionStep] || 'Соединение...'}
                        </span>
                        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden relative">
                          <motion.div 
                            className="h-full bg-gradient-to-r from-sky-500 via-cyan-400 to-indigo-500 rounded-full shadow-[0_0_8px_rgba(56,189,248,0.5)]"
                            initial={{ width: '0%' }}
                            animate={{ width: `${getStepProgress(connectionStep)}%` }}
                            transition={{ type: 'spring', stiffness: 85, damping: 15 }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="tabular-nums tracking-widest text-emerald-400 font-semibold bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/15 shadow-sm font-mono">
                        {formatTime(duration)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Gradient shadow for controls */}
            {isShowRemoteVideo && (
              <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/50 to-transparent z-10 pointer-events-none" />
            )}

            {/* Local Video Stream PIP */}
            {call.type === 'video' && localStream && !videoOff && (
              <motion.div 
                drag={!isMobile}
                dragConstraints={{ left: -300, right: 300, top: -300, bottom: 300 }}
                dragElastic={0}
                dragMomentum={false}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                className={`absolute rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.5),_0_0_25px_rgba(56,189,248,0.15)] border border-white/15 bg-zinc-950/80 backdrop-blur-md z-30 pointer-events-auto cursor-move transition-shadow duration-300 ${
                  isFluidLayout 
                    ? 'bottom-28 right-6 w-28 aspect-[3/4]'
                    : 'bottom-24 right-4 w-20 aspect-[3/4]'
                }`}
              >
                <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
              </motion.div>
            )}

            {/* Footer Control Panel */}
            <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-auto px-5 py-3.5 rounded-[24px] backdrop-blur-xl bg-black/35 border border-white/[0.08] shadow-[0_15px_35px_rgba(0,0,0,0.5)] flex items-center transition-all duration-300 ${
              isFluidLayout && !controlsVisible ? 'opacity-0 translate-y-6 pointer-events-none' : 'opacity-100 translate-y-0'
            }`}>
              <div className="flex items-center gap-4 sm:gap-5">
                {call.type === 'video' && (
                  <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleVideo}
                    className={`w-13 h-13 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md ${
                      videoOff ? 'bg-white text-black' : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-md border border-white/10'
                    }`}
                  >
                    {videoOff ? <VideoOff className="w-5.5 h-5.5" /> : <Video className="w-5.5 h-5.5" />}
                  </motion.button>
                )}
                
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={toggleMute}
                  className={`w-13 h-13 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md ${
                    muted ? 'bg-white text-black' : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-md border border-white/10'
                  }`}
                >
                  {muted ? <MicOff className="w-5.5 h-5.5" /> : <Mic className="w-5.5 h-5.5" />}
                </motion.button>

                {call.type === 'video' && !isMobile && (
                  <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleScreenShare}
                    className={`w-13 h-13 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md ${
                      isScreenSharing ? 'bg-white text-black' : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-md border border-white/10'
                    }`}
                  >
                    <MonitorUp className="w-5.5 h-5.5" />
                  </motion.button>
                )}

                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleEnd}
                  className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center transition-all shadow-lg shadow-rose-500/20 cursor-pointer"
                >
                  <PhoneOff className="w-5.5 h-5.5" />
                </motion.button>
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default VideoCall;
